// utils/routeDataUtils.js

/**
 * Extract the display name for a stop using multiple fallbacks
 * @param {Object} stop - Stop object
 * @param {Array} sites - Array of all sites
 * @returns {string} Display name for the stop
 */
export const extractStopName = (stop, sites) => {
  if (!stop) return 'Unknown Stop';
  
  // Try direct name fields in order of preference
  const directName = stop.jobName || stop.clientName || stop.name || stop.siteName;
  if (directName) return directName;
  
  // Try to find in sites array if we have a siteId
  if (stop.siteId && sites && Array.isArray(sites)) {
    const site = sites.find(s => s._id === stop.siteId);
    if (site) {
      return site.name || site.jobName || site.clientName || 'Unknown Stop';
    }
  }
  
  return 'Unknown Stop';
};

/**
 * Process and enrich route stops with additional data
 * @param {Object} route - Route object
 * @param {Array} sites - Array of all sites
 * @returns {Array} Processed stops with enriched data
 */
export const processRouteStops = (route, sites) => {
  if (!route?.stops || route.stops.length === 0) return [];
  
  // Sort stops by order field if it exists
  const sortedStops = [...route.stops].sort((a, b) => {
    if (a.order != null && b.order != null) {
      return a.order - b.order;
    }
    return 0; // Keep original order if no order field
  });
  
  return sortedStops.map((stop, index) => ({
    ...stop,
    displayName: extractStopName(stop, sites),
    orderNumber: index + 1,
    totalStops: route.stops.length,
    hasOptimizedTimes: stop.estimatedArrivalTime != null && stop.estimatedDepartureTime != null,
    isFirstStop: index === 0,
    isLastStop: index === sortedStops.length - 1
  }));
};

/**
 * Group weekly routes by day
 * @param {Array} weeklyRoutes - Array of weekly route data
 * @returns {Object} Routes grouped by day
 */
export const groupRoutesByDay = (weeklyRoutes) => {
  if (!Array.isArray(weeklyRoutes)) return {};
  
  return weeklyRoutes.reduce((acc, dayRoute) => {
    if (dayRoute.day) {
      acc[dayRoute.day] = dayRoute.routes || [];
    }
    return acc;
  }, {});
};

/**
 * Find a specific crew's route from a list of routes
 * @param {Array} routes - Array of routes
 * @param {string} crewId - Crew ID to find
 * @returns {Object|null} Found route or null
 */
export const findRouteForCrew = (routes, crewId) => {
  if (!Array.isArray(routes) || !crewId) return null;
  
  return routes.find(route => route.crewId === crewId) || null;
};

/**
 * Validate that a route has all required fields
 * @param {Object} route - Route to validate
 * @returns {Object} Validation result
 */
export const validateRouteData = (route) => {
  const errors = [];
  
  if (!route) {
    return { isValid: false, errors: ['Route is null or undefined'] };
  }
  
  if (!route._id) errors.push('Route missing _id');
  if (!route.crewId) errors.push('Route missing crewId');
  if (!Array.isArray(route.stops)) errors.push('Route stops is not an array');
  
  // Validate each stop
  if (Array.isArray(route.stops)) {
    route.stops.forEach((stop, index) => {
      if (!stop.siteId && !stop._id) {
        errors.push(`Stop ${index + 1} missing siteId or _id`);
      }
    });
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Calculate metrics for a route
 * @param {Object} route - Route object
 * @returns {Object} Route metrics
 */
export const calculateRouteMetrics = (route) => {
  if (!route) {
    return {
      stopCount: 0,
      totalHours: 0,
      serviceHours: 0,
      driveHours: 0,
      efficiency: 0,
      milesDriven: 0
    };
  }
  
  const stopCount = route.stops?.length || 0;
  const serviceHours = (route.totalServiceTime || 0) / 60;
  const driveHours = (route.totalDriveTime || 0) / 60;
  const totalHours = serviceHours + driveHours;
  
  // Efficiency is service time as percentage of total time
  const efficiency = totalHours > 0 ? Math.round((serviceHours / totalHours) * 100) : 0;
  
  return {
    stopCount,
    totalHours: Math.round(totalHours * 10) / 10, // Round to 1 decimal
    serviceHours: Math.round(serviceHours * 10) / 10,
    driveHours: Math.round(driveHours * 10) / 10,
    efficiency,
    milesDriven: route.totalMilesDriven || 0
  };
};

/**
 * Sort stops by various criteria
 * @param {Array} stops - Array of stops
 * @param {string} sortBy - Sort criteria: 'order', 'arrival', 'name'
 * @returns {Array} Sorted stops
 */
export const sortStops = (stops, sortBy = 'order') => {
  if (!Array.isArray(stops)) return [];
  
  const sorted = [...stops];
  
  switch (sortBy) {
    case 'order':
      return sorted.sort((a, b) => (a.order || 0) - (b.order || 0));
      
    case 'arrival':
      return sorted.sort((a, b) => 
        (a.estimatedArrivalTime || 0) - (b.estimatedArrivalTime || 0)
      );
      
    case 'name':
      return sorted.sort((a, b) => 
        (a.displayName || '').localeCompare(b.displayName || '')
      );
      
    default:
      return sorted;
  }
};

/**
 * Check if a route needs re-optimization
 * @param {Object} route - Route object
 * @param {number} hoursThreshold - Hours since last optimization (default 24)
 * @returns {boolean} True if needs re-optimization
 */
export const needsReoptimization = (route, hoursThreshold = 24) => {
  if (!route) return false;
  
  // Never optimized
  if (!route.isOptimized || !route.lastOptimized) return true;
  
  // Check time since last optimization
  const hoursSinceOptimization = 
    (Date.now() - new Date(route.lastOptimized).getTime()) / (1000 * 60 * 60);
  
  return hoursSinceOptimization > hoursThreshold;
};

/**
 * Filter routes by various criteria
 * @param {Array} routes - Array of routes
 * @param {Object} filters - Filter criteria
 * @returns {Array} Filtered routes
 */
export const filterRoutes = (routes, filters = {}) => {
  if (!Array.isArray(routes)) return [];
  
  return routes.filter(route => {
    // Filter by crew
    if (filters.crewId && route.crewId !== filters.crewId) return false;
    
    // Filter by optimization status
    if (filters.optimizedOnly && !route.isOptimized) return false;
    
    // Filter by minimum stops
    if (filters.minStops && (route.stops?.length || 0) < filters.minStops) return false;
    
    // Filter by date
    if (filters.date) {
      const routeDate = new Date(route.date).toDateString();
      const filterDate = new Date(filters.date).toDateString();
      if (routeDate !== filterDate) return false;
    }
    
    return true;
  });
};

/**
 * Merge two routes (useful for combining optimized results with existing data)
 * @param {Object} existingRoute - Existing route
 * @param {Object} optimizedRoute - Optimized route data
 * @returns {Object} Merged route
 */
export const mergeRoutes = (existingRoute, optimizedRoute) => {
  if (!existingRoute) return optimizedRoute;
  if (!optimizedRoute) return existingRoute;
  
  return {
    ...existingRoute,
    stops: optimizedRoute.stops || existingRoute.stops,
    totalDriveTime: optimizedRoute.totalDriveTime ?? existingRoute.totalDriveTime,
    totalServiceTime: optimizedRoute.totalServiceTime ?? existingRoute.totalServiceTime,
    totalMilesDriven: optimizedRoute.totalMilesDriven ?? existingRoute.totalMilesDriven,
    isOptimized: optimizedRoute.isOptimized ?? existingRoute.isOptimized,
    lastOptimized: optimizedRoute.lastOptimized || existingRoute.lastOptimized
  };
};

/**
 * Extract unique sites from multiple routes
 * @param {Array} routes - Array of routes
 * @returns {Array} Unique sites
 */
export const extractUniqueSites = (routes) => {
  if (!Array.isArray(routes)) return [];
  
  const siteMap = new Map();
  
  routes.forEach(route => {
    if (Array.isArray(route.stops)) {
      route.stops.forEach(stop => {
        const siteId = stop.siteId || stop._id;
        if (siteId && !siteMap.has(siteId)) {
          siteMap.set(siteId, stop);
        }
      });
    }
  });
  
  return Array.from(siteMap.values());
};